"use client";

import { useState } from "react";
import { Settings2 } from "lucide-react";
import { useLGSBrowseFilters } from "modules/lgs/hooks/useLGSBrowseFilters";
import { useLGSCardFacets } from "modules/lgs/hooks/useLGSCardFacets";
import { useFacetSettings } from "modules/collections/details/hooks/useFacetSettings";
import { FacetList } from "modules/collections/details/components/facets/FacetList";
import { FacetSettingsModal } from "modules/collections/details/components/facets/FacetSettingsModal";
import { FacetSidebarSkeleton } from "modules/collections/details/components/skeletons/SidebarSkeleton";
import type { TradeListType } from "modules/tradeLists/model/tradeList";
import type { GameId } from "modules/common/model/models";
import type { LGSCardsPagedRequest } from "modules/lgs/model/lgs";

interface LGSFacetSidebarProps {
  storeId: number;
  listType: TradeListType;
  games?: GameId[];
  /** When provided, used for the facets query key to match a server prefetch. */
  facetFilters?: LGSCardsPagedRequest;
}

export function LGSFacetSidebar({
  storeId,
  listType,
  games,
  facetFilters,
}: LGSFacetSidebarProps) {
  const { filters, toggleFacetValue, setFacet, setFacets } =
    useLGSBrowseFilters({ storeId, listType, games });
  const { facets, currency, isLoading } = useLGSCardFacets(facetFilters ?? filters);
  const { settings, moveUp, moveDown, toggleHidden, reorder, resetToDefaults } =
    useFacetSettings("lgsCards", "trade");
  const [settingsOpen, setSettingsOpen] = useState(false);

  if (isLoading) {
    return <FacetSidebarSkeleton />;
  }

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between px-2 py-1">
        <h3 className="text-base-content text-base font-semibold">Filters</h3>
        <button
          type="button"
          className="btn btn-ghost btn-xs btn-square"
          onClick={() => setSettingsOpen(true)}
          aria-label="Filter settings"
          title="Filter settings"
        >
          <Settings2 size={14} />
        </button>
      </div>

      <FacetList
        facets={facets}
        filters={filters}
        toggleFacetValue={toggleFacetValue}
        setFacet={setFacet}
        setFacets={setFacets}
        settings={settings}
        currency={currency}
        context="trade"
      />

      <FacetSettingsModal
        context="trade"
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        order={settings.order}
        hidden={settings.hidden}
        onMoveUp={moveUp}
        onMoveDown={moveDown}
        onToggleHidden={toggleHidden}
        onReorder={reorder}
        onReset={resetToDefaults}
      />
    </div>
  );
}
